import { CommandProps, Extension } from '@tiptap/core';
import type { Mark } from '@tiptap/pm/model';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textCase: {
      setLowerCase(): ReturnType;
      setSentenceCase(): ReturnType;
    };
  }
}

type SelectedTextSlice = {
  from: number;
  to: number;
  text: string;
  marks: readonly Mark[];
};

// The selection is rewritten slice by slice using positions taken from the original document, so a character whose
// converted form has a different length (e.g. 'İ') is left as-is rather than shifting every position after it.
const toSameLengthLowerCase = (character: string): string => {
  const lowerCased = character.toLowerCase();
  return lowerCased.length === character.length ? lowerCased : character;
};

const toSameLengthUpperCase = (character: string): string => {
  const upperCased = character.toUpperCase();
  return upperCased.length === character.length ? upperCased : character;
};

// The first letter of the text, of a line, or of a sentence, along with any punctuation or digits leading
// up to it (e.g. `1. `, `"`).
const SENTENCE_START_REGEX = /(^|[.!?]\s+|\n\s*)([^\p{L}\n]*)(\p{Ll})/gu;

export const toLowerCaseText = (text: string): string =>
  text.replace(/\p{Lu}/gu, toSameLengthLowerCase);

export const toSentenceCaseText = (text: string): string =>
  toLowerCaseText(text).replace(
    SENTENCE_START_REGEX,
    (_match, sentenceStart, leadingCharacters, letter) =>
      `${sentenceStart}${leadingCharacters}${toSameLengthUpperCase(letter)}`,
  );

const getSelectedTextSlices = ({
  state,
}: Pick<CommandProps, 'state'>): SelectedTextSlice[] => {
  const { from, to } = state.selection;
  const slices: SelectedTextSlice[] = [];

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText || !node.text) return;

    const sliceFrom = Math.max(pos, from);
    const sliceTo = Math.min(pos + node.nodeSize, to);
    if (sliceTo <= sliceFrom) return;

    slices.push({
      from: sliceFrom,
      to: sliceTo,
      text: node.text.slice(sliceFrom - pos, sliceTo - pos),
      marks: node.marks,
    });
  });

  return slices;
};

const transformSelectedText =
  (transform: (text: string) => string) =>
  ({ state, tr, dispatch }: CommandProps): boolean => {
    if (state.selection.empty) return false;

    const slices = getSelectedTextSlices({ state });
    if (!slices.length) return false;

    // The selection is transformed as a single string so sentences are detected across mark boundaries. A gap between
    // two slices is a line or block break which starts a new sentence.
    const offsets: number[] = [];
    let selectedText = '';
    slices.forEach((slice, index) => {
      if (index > 0 && slice.from !== slices[index - 1].to) {
        selectedText += '\n';
      }
      offsets.push(selectedText.length);
      selectedText += slice.text;
    });

    const transformedText = transform(selectedText);
    if (transformedText === selectedText) return false;

    if (dispatch) {
      slices.forEach((slice, index) => {
        const text = transformedText.slice(
          offsets[index],
          offsets[index] + slice.text.length,
        );
        if (text !== slice.text) {
          tr.replaceWith(
            slice.from,
            slice.to,
            state.schema.text(text, slice.marks),
          );
        }
      });
      dispatch(tr);
    }

    return true;
  };

export const TextCaseExtension = Extension.create({
  name: 'textCase',
  addCommands: () => {
    return {
      setLowerCase: () => transformSelectedText(toLowerCaseText),
      setSentenceCase: () => transformSelectedText(toSentenceCaseText),
    };
  },
});
